
import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchServices, type ServiceWithCategory, type ServiceRow } from '@/lib/queries/services';
import type { ServiceUpdate } from '@sylva/shared-types/database';

export type SortOption = 'title' | 'category' | 'duration' | 'price' | 'created_at';
export type SortDirection = 'asc' | 'desc';

// Use the generated type from shared-types with the category join
export type Service = ServiceWithCategory;

export const useServices = (tenantId: string | null) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('category');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['services', tenantId],
    queryFn: async () => {
      if (!tenantId || !user) return [] as Service[];
      return fetchServices(tenantId);
    },
    enabled: !!tenantId && !!user,
    staleTime: 30 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  const services = useMemo(() => (data || []) as unknown as Service[], [data]);

  const createService = async (service: Omit<ServiceRow, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      const { data, error } = await supabase
        .from('appointment_types')
        .insert([{ ...service, tenant_id: tenantId }])
        .select()
        .single();

      if (error) throw error;
      // Update cache optimistically
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return [data, ...(old || [])];
      });
      return { data, error: null as any };
    } catch (error) {
      console.error('Error creating service:', error);
      return { data: null as any, error: error as any };
    }
  };

  const updateService = async (id: string, updates: ServiceUpdate) => {
    try {
      const { data, error } = await supabase
        .from('appointment_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      // Update cache
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return (old || []).map((at: any) => at.id === id ? data : at);
      });
      return { data, error: null as any };
    } catch (error) {
      console.error('Error updating service:', error);
      return { data: null as any, error: error as any };
    }
  };

  const deleteService = async (id: string) => {
    try {
      const deletedAt = new Date().toISOString();
      // Soft delete: set deleted_at timestamp instead of hard delete
      const { error } = await supabase
        .from('appointment_types')
        .update({ deleted_at: deletedAt })
        .eq('id', id);

      if (error) throw error;
      // Update cache - mark service as deleted
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return (old || []).map((at: any) =>
          at.id === id ? { ...at, deleted_at: deletedAt } : at
        );
      });
      return { error: null as any };
    } catch (error) {
      console.error('Error deleting service:', error);
      return { error };
    }
  };

  const duplicateService = async (originalService: Service) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      // Create a new service with "Copy of" prefix and reset timestamps
      const duplicatedService = {
        ...originalService,
        title: `${t('copyOf')} ${originalService.title}`,
        tenant_id: tenantId,
        // Remove id and timestamps as they will be auto-generated
      };

      // Remove fields that shouldn't be duplicated
      const { id: _id, created_at: _created_at, updated_at: _updated_at, appointment_type_categories: _categories, ...serviceData } = duplicatedService;

      const { data, error } = await supabase
        .from('appointment_types')
        .insert([serviceData])
        .select()
        .single();

      if (error) throw error;
      // Update cache
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return [data, ...(old || [])];
      });
      return { data, error: null as any };
    } catch (error) {
      console.error('Error duplicating service:', error);
      return { data: null as any, error: error as any };
    }
  };

  const bulkDeleteServices = async (serviceIds: string[]) => {
    if (!user || !tenantId) return { error: 'Not authenticated or no tenant selected' };

    try {
      const deletedAt = new Date().toISOString();
      // Soft delete: set deleted_at timestamp instead of hard delete
      const { error } = await supabase
        .from('appointment_types')
        .update({ deleted_at: deletedAt })
        .in('id', serviceIds)
        .eq('tenant_id', tenantId);

      if (error) throw error;
      // Update cache - mark services as deleted
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return (old || []).map((at: any) =>
          serviceIds.includes(at.id) ? { ...at, deleted_at: deletedAt } : at
        );
      });
      return { error: null as any };
    } catch (error) {
      console.error('Error bulk deleting services:', error);
      return { error };
    }
  };

  const bulkDuplicateServices = async (originalServices: Service[]) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      const duplicatedServices = originalServices.map(service => {
        const { id: _id, created_at: _created_at, updated_at: _updated_at, appointment_type_categories: _categories, ...serviceData } = service;
        return {
          ...serviceData,
          title: `${t('copyOf')} ${service.title}`,
          tenant_id: tenantId,
        };
      });

      const { data, error } = await supabase
        .from('appointment_types')
        .insert(duplicatedServices)
        .select();

      if (error) throw error;
      // Update cache
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return [...(data || []), ...(old || [])];
      });
      return { data, error: null as any };
    } catch (error) {
      console.error('Error bulk duplicating services:', error);
      return { data: null as any, error: error as any };
    }
  };

  const bulkCreateServices = async (servicesToCreate: (Omit<ServiceRow, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'deleted_at'> | (Omit<ServiceRow, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'deleted_at' | 'category_id'> & { category?: string }))[]) => {
    if (!user || !tenantId) return { data: null, error: 'Not authenticated or no tenant selected' };

    try {
      // First, collect all unique category names and create/find categories
      const uniqueCategories = [...new Set(servicesToCreate
        .map(at => (at as any).category) // CSV import data temporarily has category field
        .filter(category => category && category.trim() !== '')
      )];

      const categoryMap = new Map<string, string>(); // category name -> category_id

      // Create or find categories
      for (const categoryName of uniqueCategories) {
        try {
          const { data: existingCategory } = await supabase
            .from('appointment_type_categories')
            .select('id, name')
            .eq('tenant_id', tenantId)
            .ilike('name', categoryName)
            .single();

          if (existingCategory) {
            categoryMap.set(categoryName.toLowerCase(), existingCategory.id);
          } else {
            // Create new category
            const { data: newCategory, error: categoryError } = await supabase
              .from('appointment_type_categories')
              .insert([{
                name: categoryName.trim(),
                tenant_id: tenantId,
                display_order: uniqueCategories.indexOf(categoryName),
                is_active: true,
                description: null,
                icon: null,
              }])
              .select('id, name')
              .single();

            if (categoryError) throw categoryError;
            if (newCategory) {
              categoryMap.set(categoryName.toLowerCase(), newCategory.id);
            }
          }
        } catch (error) {
          console.warn(`Could not create/find category "${categoryName}":`, error);
        }
      }

      // Now create services with proper category_id
      const servicesWithTenant = servicesToCreate.map(service => {
        const category = (service as any).category; // CSV import data temporarily has category field
        const categoryId = category 
          ? categoryMap.get(category.toLowerCase()) || null
          : null;

        // Remove the legacy category field from the data
        const { category: _, ...serviceWithoutCategory } = service as any;

        return {
          ...serviceWithoutCategory,
          tenant_id: tenantId,
          category_id: categoryId,
        };
      });

      const { data, error } = await supabase
        .from('appointment_types')
        .insert(servicesWithTenant)
        .select(`
          *,
          appointment_type_categories (
            id,
            name,
            display_order
          )
        `);

      if (error) throw error;
      // Update cache
      queryClient.setQueryData(['services', tenantId], (old: any[] | undefined) => {
        return [...(data || []), ...(old || [])];
      });
      return { data, error: null as any, successful: data.length, duplicates: 0, failed: 0 };
    } catch (error) {
      console.error('Error bulk creating services:', error);
      return { data: null as any, error: error as any };
    }
  };

  const filteredAndSortedServices = useMemo(() => {
    let filtered = services;

    // Filter deleted services by default
    if (!showDeleted) {
      filtered = filtered.filter(service => !service.deleted_at);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(query) ||
        (service.description ?? '').toLowerCase().includes(query) ||
        (service.appointment_type_categories?.name || '').toLowerCase().includes(query)
      );
    }

    // Filter by categories
    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter(service =>
        service.category_id && selectedCategoryIds.includes(service.category_id)
      );
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number | Date = '';
      let bValue: string | number | Date = '';

      switch (sortBy) {
        case 'title':
          aValue = a.title;
          bValue = b.title;
          break;
        case 'category':
          aValue = a.appointment_type_categories?.name || '';
          bValue = b.appointment_type_categories?.name || '';
          break;
        case 'duration':
          aValue = a.duration;
          bValue = b.duration;
          break;
        case 'price':
          aValue = a.price ?? 0;
          bValue = b.price ?? 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (sortDirection === 'asc') {
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return aValue - bValue;
        }
        if (aValue instanceof Date && bValue instanceof Date) {
          return aValue.getTime() - bValue.getTime();
        }
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return bValue - aValue;
        }
        if (aValue instanceof Date && bValue instanceof Date) {
          return bValue.getTime() - aValue.getTime();
        }
        return bValue.toString().localeCompare(aValue.toString());
      }
    });

    return sorted;
  }, [services, searchQuery, selectedCategoryIds, sortBy, sortDirection, showDeleted]);

  return {
    services: filteredAndSortedServices,
    allServices: services,
    isLoading,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    selectedCategoryIds,
    setSelectedCategoryIds,
    showDeleted,
    setShowDeleted,
    createService,
    updateService,
    deleteService,
    duplicateService,
    bulkDeleteServices,
    bulkDuplicateServices,
    bulkCreateServices,
    refetch,
  };
};
